import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { useIsCurrentUserAnAdmin } from "@theprelude/core";
import {
  BlockListIcon,
  DropdownContent,
  DropdownRadio,
  DropdownRadioGroup,
  Dropdown,
  DropdownTrigger,
  IconButton,
  LoadingPage,
} from "@theprelude/ds";
import { ReactNode, Suspense } from "react";
import { setPageTitle, useTitleStore } from "@/components/app-shell/app-shell";
import { Flag } from "@/components/flag/flag";
import { usePopper } from "@/hooks/use-popper";
import { FileRoutesByTo } from "@/routeTree.gen";

export const Route = createFileRoute("/_app/account")({
  beforeLoad: () => setPageTitle("Account"),
  component: AccountLayout,
});

function AccountLayout() {
  const { data: isAdmin } = useIsCurrentUserAnAdmin();
  const dropdown = usePopper();
  const title = useTitleStore((state) => state.value);

  return (
    <section className="flex flex-1 flex-col overflow-hidden bg-primary-background sm:flex-row">
      <aside className="hidden w-[20.125rem] shrink-0 flex-col px-4 py-6 dark:border-r dark:border-stroke-line sm:flex">
        <nav>
          <AccountLink to="/account">Account Settings</AccountLink>
          <AccountLink to="/account/profile">My Profile</AccountLink>
          <AccountLink to="/account/users">Account Users</AccountLink>
          <AccountLink to="/account/integrations">Integrations</AccountLink>
          {isAdmin && (
            <AccountLink to="/account/oidc">
              OpenID Connect Settings
            </AccountLink>
          )}
          <Flag features={["policy_evaluator"]}>
            <AccountLink to="/account/alerts">Alerts</AccountLink>
            <AccountLink to="/account/notifications">Notifications</AccountLink>
          </Flag>
        </nav>
      </aside>
      <div className="flex items-center justify-end p-3 sm:hidden">
        <Dropdown {...dropdown}>
          <DropdownTrigger asChild>
            <IconButton icon={<BlockListIcon />} />
          </DropdownTrigger>
          <DropdownContent align="end">
            <DropdownRadioGroup value={title}>
              <Link to="/account">
                <DropdownRadio value="Account">Account Settings</DropdownRadio>
              </Link>
              <Link to="/account/profile">
                <DropdownRadio value="Profile">My Profile</DropdownRadio>
              </Link>
              <Link to="/account/users">
                <DropdownRadio value="Users">Account Users</DropdownRadio>
              </Link>
              <Link to="/account/integrations">
                <DropdownRadio value="Integrations">Integrations</DropdownRadio>
              </Link>
              {isAdmin && (
                <Link to="/account/oidc">
                  <DropdownRadio value="OpenID Settings">
                    OpenID Connect Settings
                  </DropdownRadio>
                </Link>
              )}
            </DropdownRadioGroup>
          </DropdownContent>
        </Dropdown>
      </div>
      <main className="flex w-full flex-1 flex-col overflow-auto bg-surface-background">
        <Suspense fallback={<LoadingPage />}>
          <Outlet />
        </Suspense>
      </main>
    </section>
  );
}

const AccountLink = ({
  to,
  children,
}: {
  to: keyof FileRoutesByTo;
  children: ReactNode;
}) => (
  <Link
    to={to}
    className="flex h-[2.5rem] items-center p-2"
    activeOptions={{
      exact: true,
      includeSearch: false,
      includeHash: false,
    }}
    activeProps={{
      className: "bg-tertiary-foreground font-medium",
    }}
  >
    {children}
  </Link>
);
